import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListLeadScoreRulesResult } from '@whinerp/client';
import { Button, ErrorState, Field, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * The rules a workspace scores leads by (G-051).
 *
 * Every rule shows its points beside its name, including the negative ones. A
 * workspace has to be able to say what it does not want - "free email domain,
 * minus twenty" - and a model that can only add makes every lead look better
 * than the last.
 *
 * Cooling is a number of points per week, and the default is zero. A workspace
 * that has not thought about it should not discover its leads silently losing
 * points and sinking down a queue sorted by score.
 *
 * Changing anything offers a rescore rather than doing one. The queue reads
 * the STORED score, so a model nobody applied is a model nobody sees - and
 * rescoring every lead is a write across the whole table, which is somebody's
 * decision rather than a side effect of typing in a box.
 */
export function LeadScoringPanel(): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [points, setPoints] = useState('10');
  const [field, setField] = useState('email');

  const model = useQuery<ListLeadScoreRulesResult, ApiError>({
    queryKey: ['lead-score-rules'],
    queryFn: () => api.listLeadScoreRules({}),
  });

  const refresh = (): Promise<unknown> =>
    queryClient.invalidateQueries({ queryKey: ['lead-score-rules'] });

  const add = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.saveLeadScoreRule({
        body: {
          label: label.trim(),
          // The simplest condition that is still a real RuleGroup: "this field
          // has a value". Anything richer belongs in the rule builder, which
          // is the same component a segment uses.
          conditions: { all: [{ field, operator: 'is-set' }] },
          points: Number(points),
        },
      }),
    onSuccess: async () => {
      setLabel('');
      await refresh();
    },
  });

  const retire = useMutation<unknown, ApiError, string>({
    mutationFn: (ruleId) => api.deleteLeadScoreRule({ path: { ruleId } }),
    onSuccess: () => refresh(),
  });

  const decay = useMutation<unknown, ApiError, number>({
    mutationFn: (pointsPerWeek) => api.setLeadScoreDecay({ body: { pointsPerWeek } }),
    onSuccess: () => refresh(),
  });

  const rescore = useMutation<{ rescored: number }, ApiError>({
    mutationFn: () => api.rescoreLeads({}),
    onSuccess: () => refresh(),
  });

  const failure = add.error ?? retire.error ?? decay.error ?? rescore.error;

  if (model.isPending) return <Skeleton className="h-block-md w-full" />;
  if (model.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={model.error.message}
        onRetry={() => void model.refetch()}
      />
    );
  }

  return (
    <Panel data-region="lead-scoring" id="lead-scoring">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('lead.scoring.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('lead.scoring.body')}</p>

        {failure ? (
          <p data-scoring-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {failure.message}
          </p>
        ) : null}

        <ul className="flex flex-col">
          {model.data.rules.map((rule) => (
            <li
              key={rule.ruleId}
              data-score-rule={rule.ruleId}
              className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
            >
              <span className="min-w-0 flex-1 truncate text-body text-ink">{rule.label}</span>
              {/* The sign is the information. A workspace saying what it does
                  not want needs to see that it said so. */}
              <Num
                data-score-points={rule.ruleId}
                className={rule.points < 0 ? 'text-danger-text' : 'text-success-text'}
              >
                {rule.points > 0 ? `+${String(rule.points)}` : String(rule.points)}
              </Num>
              <Button
                intent="ghost"
                size="small"
                data-score-remove={rule.ruleId}
                onClick={() => retire.mutate(rule.ruleId)}
              >
                {t('action.remove')}
              </Button>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('lead.scoring.label')}
            data-score-label
            value={label}
            onChange={(event) => setLabel(event.currentTarget.value)}
          />
          <label className="flex flex-col gap-1">
            <span className="text-micro uppercase tracking-label text-ink-label">
              {t('lead.scoring.when')}
            </span>
            <select
              data-score-field
              value={field}
              onChange={(event) => setField(event.target.value)}
              className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
            >
              {['email', 'phone', 'company_name', 'title', 'source'].map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </label>
          <Field
            label={t('lead.scoring.points')}
            data-score-points-input
            value={points}
            onChange={(event) => setPoints(event.currentTarget.value)}
          />
          <Button
            intent="secondary"
            size="small"
            data-score-add
            busy={add.isPending}
            onClick={() => {
              if (label.trim() !== '' && Number.isInteger(Number(points))) add.mutate();
            }}
          >
            {t('action.add')}
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-micro uppercase tracking-label text-ink-label">
              {t('lead.scoring.decay')}
            </span>
            <select
              data-score-decay
              value={String(model.data.decayPerWeek)}
              onChange={(event) => decay.mutate(Number(event.target.value))}
              className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
            >
              {[0, 5, 10, 20].map((value) => (
                <option key={value} value={value}>
                  {value === 0
                    ? t('lead.scoring.decay.none')
                    : t('lead.scoring.decay.perWeek', { values: { points: String(value) } })}
                </option>
              ))}
            </select>
          </label>
          {/*
            Offered, not done automatically. Rescoring writes every lead in the
            workspace, which is somebody's decision rather than a side effect
            of typing in a box - and the queue reads the stored score, so the
            button is how a model becomes visible.
          */}
          <Button
            intent="secondary"
            size="small"
            data-score-rescore
            busy={rescore.isPending}
            onClick={() => rescore.mutate()}
          >
            {t('lead.scoring.rescore')}
          </Button>
          {rescore.data ? (
            <span data-score-rescored className="text-meta text-ink-meta">
              {t('lead.scoring.rescored', { values: { count: String(rescore.data.rescored) } })}
            </span>
          ) : null}
        </div>
      </div>
    </Panel>
  );
}
